import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Saint-Jean-de-Védas' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Saint-Jean-de-Védas',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Saint-Jean-de-Védas' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Saint-Jean-de-Védas. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous dans le quartier Mas de Grille à Saint-Jean-de-Védas ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons dans tout Saint-Jean-de-Védas : Mas de Grille, centre-ville, zones pavillonnaires et secteur du tramway. Nos techniciens se déplacent sous 24 à 48h, 7j/7.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les volets roulants des pavillons de Saint-Jean-de-Védas ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Les pavillons de Saint-Jean-de-Védas sont très souvent équipés de volets roulants. Nous réparons moteurs, sangles, lames et assurons la motorisation. Devis gratuit, garantie 10 ans.',
      },
    },
  ],
};

export default function SaintJeanDeVedasPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante St-Jean-Védas | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Saint-Jean-de-Védas ? DFPRO intervient 24-48h, 7j/7. Mas de Grille, tramway. Devis gratuit."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-saint-jean-de-vedas/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Saint-Jean-de-Védas"
        subtitle="Pavillonnaire et résidentiel au sud-ouest de Montpellier : nos techniciens réparent vos baies, volets et vitrages à Saint-Jean-de-Védas. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Volet roulant ou baie en panne à Saint-Jean-de-Védas ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Saint-Jean-de-Védas, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Saint-Jean-de-Védas, au sud-ouest de Montpellier, est une commune résidentielle et pavillonnaire. Nous intervenons sur tous types de pannes : baie coulissante qui coince, volet roulant bloqué, vitre à remplacer. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/stjeanvedas-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Saint-Jean-de-Védas"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : entretien et réparation</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les pavillons de Saint-Jean-de-Védas sont souvent équipés de baies coulissantes donnant sur le jardin. Quand les roulettes s'usent, la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">devient dure à ouvrir</Link> et peut dérailler.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>. Toutes marques.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : la panne classique du pavillon</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Dans ce secteur pavillonnaire, le <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volet roulant</Link> est la pièce qui tombe le plus souvent en panne. <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Blocage</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> HS, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link> cassée.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous assurons la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels et la réparation de tous types : filaires, radio, solaires.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/stjeanvedas-volet-02.jpg"
              alt="Réparation de volet roulant à Saint-Jean-de-Védas"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/stjeanvedas-vitrage-03.jpg"
              alt="Remplacement de vitrage à Saint-Jean-de-Védas"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et fenêtre : toutes réparations</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> pour <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence</Link> 7j/7.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Saint-Jean-de-Védas sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Saint-Jean-de-Védas : Mas de Grille, centre-ville, zones pavillonnaires. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-juvignac/" className="text-accent-500 font-semibold hover:underline">Juvignac</Link>, <Link to="/depannage-menuiserie-villeneuve-les-maguelone/" className="text-accent-500 font-semibold hover:underline">Villeneuve-lès-Maguelone</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}